import React, { useState, useRef, useEffect } from 'react';
import '../css/inboxChart.css';
import openLinkMediaIcon from '../../../../../../asset-controller-mod/asset-serv/images/openLinkMedia.png';

/* ── Types ───────────────────────────────────────────────── */

export interface InboxLegendItem {
  label: string;
  value: number;
  color: string;
}

export interface InboxChartProps {
  title?: string;
  totalLabel?: string;
  subLabel?: string;
  legend?: InboxLegendItem[];
  footerLabel?: string;
  footerHref?: string;
  onFooterClick?: () => void;
  onMenuClick?: () => void;
  onOpenClick?: () => void;
  onTitleClick?: () => void;
}

/* ── Defaults ────────────────────────────────────────────── */
const DEFAULT_LEGEND: InboxLegendItem[] = [
  { label: 'Business', value: 0, color: '' },
  { label: 'Sale',     value: 0, color: '' },
];

/* ── Rank-based color palette ────────────────────────────── */

const VALUE_RANK_COLORS = ['#2388FF', '#8DC1FF', '#C3DDFF', '#e3efffad'];
const BG_COLOR  = '#FFFFFF';
const GAP_RAD   = 0.18;

/* ── Enrich legend into arc segments ─────────────────────── */

interface EnrichedSegment {
  label: string;
  value: number;
  color: string;
  startRad: number;
  endRad: number;
}

function enrichSegments(legend: InboxLegendItem[], total: number): EnrichedSegment[] {
  const sortedUnique = Array.from(
    new Set(legend.map((i) => i.value))
  ).sort((a, b) => b - a);

  const PI2 = Math.PI * 2;
  const OFFSET = -Math.PI / 2;
  let accum = 0;

  return legend.map((item) => {
    const startRad = OFFSET + PI2 * (accum / total);
    const endRad = OFFSET + PI2 * ((accum + item.value) / total);
    accum += item.value;
    return {
      ...item,
      color: VALUE_RANK_COLORS[sortedUnique.indexOf(item.value)] ?? /* istanbul ignore next */ VALUE_RANK_COLORS[VALUE_RANK_COLORS.length - 1], //NOSONAR
      startRad: startRad + GAP_RAD / 2,
      endRad: endRad - GAP_RAD / 2,
    };
  });
}

/* ── Canvas constants ────────────────────────────────────── */

const SIZE      = 220;
const CX        = SIZE / 2;
const CY        = SIZE / 2;
const RADIUS    = 90;
const ARC_WIDTH = 28;
const INNER_R   = RADIUS - ARC_WIDTH / 2 - 14;

/* ── Canvas renderer ─────────────────────────────────────── */
function drawEmptyDonut(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  /* istanbul ignore next */
  if (!ctx) return;
  /* istanbul ignore next */
  const dpr = window.devicePixelRatio || 1;
  canvas.width        = SIZE * dpr;
  canvas.height       = SIZE * dpr;
  canvas.style.width  = `${SIZE}px`;
  canvas.style.height = `${SIZE}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, SIZE, SIZE);

  const segmentColor = '#DAEAFF';
  const numSegments  = 4;
  const gapAngle     = 0.18;
  const segAngle     = (Math.PI * 2 - gapAngle * numSegments) / numSegments;

  for (let i = 0; i < numSegments; i++) {
    const startAngle = -Math.PI / 2 + i * (segAngle + gapAngle) + gapAngle / 2;
    const endAngle   = startAngle + segAngle;

    ctx.beginPath();
    ctx.arc(CX, CY, RADIUS, startAngle, endAngle);
    ctx.strokeStyle = segmentColor;
    ctx.lineWidth   = ARC_WIDTH;
    ctx.lineCap     = 'butt';
    ctx.stroke();

    const CORNER_R = 6;
    const drawCap  = (angle: number) => {
      const x = CX + RADIUS * Math.cos(angle);
      const y = CY + RADIUS * Math.sin(angle);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.roundRect(-ARC_WIDTH / 2, -CORNER_R, ARC_WIDTH, CORNER_R * 2, CORNER_R);
      ctx.fillStyle = segmentColor;
      ctx.fill();
      ctx.restore();
    };
    drawCap(startAngle);
    drawCap(endAngle);
  }

  // Inner hole
  ctx.beginPath();
  ctx.arc(CX, CY, INNER_R, 0, Math.PI * 2);
  ctx.fillStyle = '#F8FAFF';
  ctx.fill();
}
function drawDonut(canvas: HTMLCanvasElement, segments: EnrichedSegment[]) {
  const ctx = canvas.getContext('2d');
  /* istanbul ignore next */
  if (!ctx) return;
  /* istanbul ignore next */
  const dpr = window.devicePixelRatio || 1;
  canvas.width = SIZE * dpr;
  canvas.height = SIZE * dpr;
  canvas.style.width = `${SIZE}px`;
  canvas.style.height = `${SIZE}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, SIZE, SIZE);

  // Background ring — full circle, no lineCap (matches media)
  ctx.beginPath();
  ctx.arc(CX, CY, RADIUS, 0, Math.PI * 2);
  ctx.strokeStyle = BG_COLOR;
  ctx.lineWidth = ARC_WIDTH;
  ctx.stroke();

  // Coloured segments — rounded caps sit on top of BG ring, gaps show between them
  segments.forEach((seg) => {
    ctx.beginPath();
    ctx.arc(CX, CY, RADIUS, seg.startRad, seg.endRad);
    ctx.strokeStyle = seg.color;
    ctx.lineWidth = ARC_WIDTH;
    ctx.lineCap = 'butt';
    ctx.stroke();

    // ── Rounded corner caps ──────────────────────────
    const CORNER_R = 6;

    const drawRoundedCap = (angle: number) => {
      const x = CX + RADIUS * Math.cos(angle);
      const y = CY + RADIUS * Math.sin(angle);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle); // ✅ align X with radial direction (across the stroke)

      ctx.beginPath();
      ctx.roundRect(
        -ARC_WIDTH / 2,  // spans the stroke width radially
        -CORNER_R,       // tiny extension along arc tangent
        ARC_WIDTH,
        CORNER_R * 2,
        CORNER_R         // only rounds the outer corners
      );
      ctx.fillStyle = seg.color;
      ctx.fill();
      ctx.restore();
    };

    drawRoundedCap(seg.startRad);
    drawRoundedCap(seg.endRad);
  });

  // Inner hole
  ctx.beginPath();
  ctx.arc(CX, CY, INNER_R, 0, Math.PI * 2);
  ctx.fillStyle = '#F8FAFF';
  ctx.fill();
}

/* ── Hit detection ───────────────────────────────────────── */

function getHoveredIndex(mouseX: number, mouseY: number, segments: EnrichedSegment[]): number | null {
  const dx = mouseX - CX;
  const dy = mouseY - CY;
  const dist = Math.sqrt(dx * dx + dy * dy); //NOSONAR

  if (dist < RADIUS - ARC_WIDTH / 2 || dist > RADIUS + ARC_WIDTH / 2) return null;

  const angle = Math.atan2(dy, dx);
  const normalise = (a: number) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const a = normalise(angle);

  /* istanbul ignore next */ 
  for (let i = 0; i < segments.length; i++) {
    const s = normalise(segments[i].startRad);
    const e = normalise(segments[i].endRad);
    const inArc = s <= e ? (a >= s && a <= e) : (a >= s || a <= e);
    /* istanbul ignore next */
    if (inArc) return i;
  }
  /* istanbul ignore next */ 
  return null;
}

const ExternalLinkIcon = () => (
  <img src={openLinkMediaIcon} width="18" height="18" alt="external link" />
);

/* ── Component ───────────────────────────────────────────── */

const InboxChart: React.FC<InboxChartProps> = ({
  title = 'Inbox',
  totalLabel = 'Total',
  subLabel = 'inbox',
  legend = DEFAULT_LEGEND,
  footerLabel = 'Total of all inbox',
  footerHref = '#',
  onFooterClick,
  onMenuClick,
  onOpenClick,
  onTitleClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const total = legend.reduce((sum, i) => sum + i.value, 0);
  const segments = enrichSegments(legend, total);

  useEffect(() => {
    /* istanbul ignore next */
    if (canvasRef.current) {
      if (total === 0) {
        drawEmptyDonut(canvasRef.current);
      } else {
        drawDonut(canvasRef.current, segments);
      }
    }
  }, [hoveredIndex, total]);
  /* istanbul ignore next */
  const updateTooltip = (index: number) => {
    const seg = segments[index];
    const midAngle = (seg.startRad + seg.endRad) / 2;
    const tipR = RADIUS; // sit on the arc centre line (matches media)
    setTooltipStyle({
      left:      CX + tipR * Math.cos(midAngle) + 20,
      top:       CY + tipR * Math.sin(midAngle),
      transform: 'translate(-50%, -50%)',
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    /* istanbul ignore next */
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const idx = getHoveredIndex(mouseX, mouseY, segments);
    setHoveredIndex(idx);
    /* istanbul ignore next */
    if (idx !== null) updateTooltip(idx);
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  return (
    <div className="scl--cms-components-inboxChart-card">

      {/* ── Header ── */}
      <div className="scl--cms-components-inboxChart-header">
        <button
          className="scl--cms-components-inboxChart-title"
          onClick={onTitleClick}
        >{title}</button>

      </div>

      {/* ── Body ── */}
      <div className="scl--cms-components-inboxChart-body">

        {/* Canvas donut */}
        <div //NOSONAR
          ref={wrapRef}
          className="scl--cms-components-inboxChart-donutWrap"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <canvas ref={canvasRef} />

          {/* Center text */}
          <div className="scl--cms-components-inboxChart-center">
            <span className="scl--cms-components-inboxChart-centerLabel">{totalLabel}</span>
            <span className="scl--cms-components-inboxChart-centerValue">{total}</span>
            <span className="scl--cms-components-inboxChart-centerSub">{subLabel}</span>
          </div>

          {/* Hover tooltip */}
          {hoveredIndex !== null && (
            /* istanbul ignore next */
            <div
              className="scl--cms-components-inboxChart-tooltip"
              style={tooltipStyle}
            >
              {segments[hoveredIndex].label}: {segments[hoveredIndex].value}
              <span className="scl--cms-components-inboxChart-tooltipBeak" />
            </div>
          )}
        </div>

        {/* Legend pills */}
        <div className="scl--cms-components-inboxChart-legend">
          {segments.map((seg) => (
            <div key={seg.label} className="scl--cms-components-inboxChart-legendItem">
              <span
                className="scl--cms-components-inboxChart-legendDot"
                style={{ background: seg.color }}
              />
              <span className="scl--cms-components-inboxChart-legendName">{seg.label}</span>
              <span className="scl--cms-components-inboxChart-legendCount">{seg.value}</span>
            </div>
          ))}
        </div>

      </div>

      {/* ── Footer ── */}
      <div className="scl--cms-components-inboxChart-footer">
        <a //NOSONAR
          href={onFooterClick ? undefined : footerHref}
          className="scl--cms-components-inboxChart-footerLink"
          onClick={onFooterClick ? (e) => { e.preventDefault(); onFooterClick(); } : undefined}
          style={{ cursor: onFooterClick ? 'pointer' : undefined }}
        >
          {footerLabel}
        </a>
        <button
          className="scl--cms-components-inboxChart-openBtn"
          onClick={onOpenClick}
        >
          Open <ExternalLinkIcon />
        </button>
      </div>

    </div>
  );
};

export default InboxChart;